import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom"
import { useEffect } from "react"
import ReactGA4 from "react-ga4"
import OnboardingPage from "./modules/onboarding/ui/pages/OnboardingPage"
import EmployeesPage from "./modules/onboarding/ui/pages/EmployeesPage"
import OffboardingPage from "./modules/offboarding/ui/pages/OffboardingPage"
import OffboardingDetailPage from "./modules/offboarding/ui/pages/OffboardingDetailPage"
import { ProtectedRoute } from "./modules/layout/ui/routing/ProtectedRoute"
import LoginPage from "./modules/auth/login/ui/pages/LoginPage"
import SignupPage from "./modules/auth/register/ui/pages/SignupPage"
import OtpPage from "./modules/auth/otp/ui/pages/OtpPage"
import ForgotPasswordPage from "./modules/auth/forgot-password/ui/pages/ForgotPasswordPage"
import ResetPasswordPage from "./modules/auth/reset-password/ui/pages/ResetPasswordPage"
import DashboardPage from "./modules/dashboard/ui/pages/DashboardPage"
import EmployeeDashboard from "./modules/employee/ui/pages/EmployeeDashboard"
import EmployeeOnboardingPage from "./modules/employee/ui/pages/EmployeeOnboardingPage"
import { EmployeeOffboardingPage } from "./modules/offboarding/ui/pages/EmployeeOffboardingPage"
import EmployeeDocumentsPage from "./modules/employee/ui/pages/EmployeeDocumentsPage"
import ReviewEmployeeInformationPage from "./modules/onboarding/ui/pages/ReviewEmployeeInformationPage"
import EmployeeLayout from "./modules/employee/ui/components/EmployeeLayout"
import VacancyPage from "./modules/recruitment/vacancies/ui/pages/VacancyPage"
import RecruitmentPage from "./modules/recruitment/ui/pages/RecruitmentPage"
import ApplicationsPage from "./modules/recruitment/applications/ui/pages/ApplicationsPage"
import HiredCandidatesPage from "./modules/recruitment/applications/ui/pages/HiredCandidatesPage"
import VacancyDetailsPage from "./modules/recruitment/vacancies/ui/pages/VacancyDetailsPage"
import BillingPlansPage from "./modules/subscriptions/ui/components/BillingPlans"
import PayfastPage from "./modules/subscriptions/ui/pages/PayfastPage"
import PaymentCancel from "./modules/subscriptions/ui/pages/PaymentCancel"
import PaymentSuccess from "./modules/subscriptions/ui/pages/PaymentSuccess"
import { ApplicantsPage } from "./modules/recruitment/applications/ui/pages/ApplicantsPage"
import { ApplicantDetailsPage } from "./modules/recruitment/applications/ui/pages/ApplicantDetailsPage"
import { CandidateDetailPage } from "./modules/recruitment/candidates/ui/CandidateDetailPage"
import InviteEmployeePage from "./modules/auth/invite/ui/pages/InviteEmployee"
import { BillingDashboard } from "./modules/subscriptions/ui/pages/BillingDashboard"
import { WalletPage } from "./modules/wallet/ui/pages/WalletPage"
import { AccountSuspended } from "./modules/subscriptions/ui/pages/AccountSuspended"
import { ActivatePlanPage } from "./modules/subscriptions/ui/pages/ActivatePlanPage"
import { SubscriptionProtectedRoute } from "./modules/subscriptions/ui/components/SubscriptionProtectedRoute"
import { ChangePlanPage } from "./modules/subscriptions/ui/pages/ChangePlan"
import ApplyPage from "./modules/public/pages/ApplyPage"
import TimeslotPage from "./modules/public/pages/TimeSlotPage"
import BookingConfirmedPage from "./modules/public/pages/BookingConfirmedPage"
import TaskCompletionPage from "./modules/public/pages/TaskCompletionPage"
import LinkedInSharePage from "./modules/recruitment/vacancies/ui/pages/LinkedInSharePage"
import { GuestRoute } from "./modules/layout/ui/routing/GuestRoute"
import CreateSubscriptionPage from "./modules/subscriptions/ui/pages/CreateSubscription"
import WorkflowManagerPage from "./modules/onboarding/workflows/ui/pages/WorkflowManagerPage"
import WorkflowBuilderPage from "./modules/onboarding/workflows/ui/pages/WorkflowBuilderPage"
import LiveWorkflowsPage from "./modules/onboarding/workflows/ui/pages/LiveWorkflowsPage"
import WorkflowDetailsPage from "./modules/onboarding/workflows/ui/pages/WorkflowDetailsPage"
import UserProfile from "./modules/profile/ui/pages/UserProfilePage"
import OrganizationProfilePage from "./modules/profile/ui/pages/OrganizationProfilePage"
import IntegrationsPage from "./modules/profile/ui/pages/IntegrationsPage"
import VideoInterviewIntroPage from "./modules/public/pages/VideoInterviewIntroPage"
import VideoInterviewPage from "./modules/public/pages/VideoInterviewPage"
import VideoInterviewSuccessPage from "./modules/public/pages/VideoInterviewSuccessPage"
import VideoInterviewFailurePage from "./modules/public/pages/VideoInterviewFailurePage"
import EmailTemplatesListPage from "./modules/email-templates/ui/pages/EmailTemplatesListPage"
import EmailTemplateEditorPage from "./modules/email-templates/ui/pages/EmailTemplateEditorPage"
import { PlanType } from "@/modules/organizations/types/plan-type"
import { AppRole } from "./modules/auth/constants/app-role"
import AuthenticatedLayout from "./modules/layout/ui/navbar/AuthenticatedLayout"
import SelectOrganisationPage from "./modules/organizations/ui/pages/SelectOrganisationPage"
import LegalDocumentPage from "./modules/legal/ui/pages/LegalDocumentPage"
import AcceptLegalDocumentsPage from "./modules/auth/register/ui/pages/AcceptLegalDocumentsPage"
import UsersListPage from "./modules/users/ui/pages/UsersListPage"
import OrganizationInvitePage from "./modules/organizations/ui/pages/OrganizationInvitePage"
import AdminDocumentsPage from "./modules/documents/ui/pages/AdminDocumentsPage"

export const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const pathname = location.pathname

  // Employee routes keep their own dedicated layout
  if (pathname.startsWith("/employee")) {
    return <EmployeeLayout>{children}</EmployeeLayout>
  }

  // All other authenticated routes use the unified layout
  return <AuthenticatedLayout>{children}</AuthenticatedLayout>
}

function RouteTracker() {
  const location = useLocation()

  useEffect(() => {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
    if (measurementId) {
      ReactGA4.send({ hitType: "pageview", page: location.pathname })
    }
  }, [location])
  return null
}

export default function App() {
  return (
    <Router>
      <RouteTracker />
      <Routes>
        {/* Public pages */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/apply/job/:applyToken" element={<ApplyPage />} />
        <Route path="/apply/:token" element={<TimeslotPage />} />
        <Route
          path="/video-interview/:token"
          element={<VideoInterviewIntroPage />}
        />
        <Route
          path="/video-interview/:token/record"
          element={<VideoInterviewPage />}
        />
        <Route
          path="/video-interview/success"
          element={<VideoInterviewSuccessPage />}
        />
        <Route
          path="/video-interview/failure"
          element={<VideoInterviewFailurePage />}
        />
        <Route path="/booking-confirmed" element={<BookingConfirmedPage />} />
        <Route path="/task/:token" element={<TaskCompletionPage />} />
        <Route path="/onboarding/tasks" element={<TaskCompletionPage />} />
        <Route
          path="/signup"
          element={
            <GuestRoute>
              <SignupPage />
            </GuestRoute>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/otp" element={<OtpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/InviteAuth" element={<InviteEmployeePage />} />
        <Route path="/billing-plans" element={<BillingPlansPage />} />
        <Route path="/payfast" element={<PayfastPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/legal/:type" element={<LegalDocumentPage />} />
        <Route
          path="/accept-legal-documents"
          element={
            <ProtectedRoute>
              <AcceptLegalDocumentsPage />
            </ProtectedRoute>
          }
        />

        <Route element={<SubscriptionProtectedRoute />} />
        {/* Organisation Selection (shown after login when no org is active) */}
        <Route
          path="/select-organisation"
          element={
            <ProtectedRoute>
              <SelectOrganisationPage />
            </ProtectedRoute>
          }
        />

        {/* Protected: Organisation Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute
              roles={[AppRole.RegisteredUser, AppRole.OrganizationOwner]}
            >
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Recruitment Page */}
        <Route
          path="/recruitment"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <RecruitmentPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Employee Pages */}
        <Route
          path="/employee/dashboard"
          element={
            <ProtectedRoute roles={[AppRole.Employee]}>
              <AppLayout>
                <EmployeeDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/onboarding"
          element={
            <ProtectedRoute roles={[AppRole.Employee]}>
              <AppLayout>
                <EmployeeOnboardingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/offboarding"
          element={
            <ProtectedRoute roles={[AppRole.Employee]}>
              <AppLayout>
                <EmployeeOffboardingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/documents"
          element={
            <ProtectedRoute roles={[AppRole.Employee]}>
              <AppLayout>
                <EmployeeDocumentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/details/:employeeId"
          element={
            <ProtectedRoute roles={[AppRole.OrganizationOwner]}>
              <AppLayout>
                <ReviewEmployeeInformationPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Onboarding Page */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <OnboardingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/workflows"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <WorkflowManagerPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/workflows/new"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <WorkflowBuilderPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/workflows/:id/edit"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <WorkflowBuilderPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/workflows/live"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <LiveWorkflowsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/workflows/live/:workflowId"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <WorkflowDetailsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UserProfile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <IntegrationsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/profile/organization/:OrganizationId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OrganizationProfilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/employees"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <EmployeesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/offboarding/:id"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <OffboardingDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/offboarding"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <OffboardingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/employees/:employeeId"
          element={
            <ProtectedRoute plan={[PlanType.Onboarding, PlanType.FullHiring]}>
              <AppLayout>
                <ReviewEmployeeInformationPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Jobs Page */}
        <Route
          path="/recruitment/jobs"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <VacancyPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/vacancies"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <VacancyPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/vacancies/:vacancyId"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <VacancyDetailsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/applications"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <ApplicationsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/applications/:applicationId"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <ApplicantDetailsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/vacancies/:vacancyId/applicants"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <ApplicantsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/candidates/:candidateId"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <CandidateDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/applications/hired"
          element={
            <ProtectedRoute plan={[PlanType.Recruitment, PlanType.FullHiring]}>
              <AppLayout>
                <HiredCandidatesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Admin Documents */}
        <Route
          path="/admin/documents"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.SuperAdmin]}
            >
              <AppLayout>
                <AdminDocumentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/users"
          element={
            <ProtectedRoute roles={[AppRole.OrganizationOwner]}>
              <AppLayout>
                <UsersListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/email-templates"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <EmailTemplatesListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/email-templates/:type"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <EmailTemplateEditorPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Account Suspended */}
        <Route
          path="/account-suspended"
          element={
            <ProtectedRoute>
              <AccountSuspended />
            </ProtectedRoute>
          }
        />

        <Route path="/invite/:token" element={<OrganizationInvitePage />} />

        {/* Activate Plan (trial expired) */}
        <Route
          path="/activate-plan"
          element={
            <ProtectedRoute>
              <ActivatePlanPage />
            </ProtectedRoute>
          }
        />

        {/* Wallet Dashboard (Auth Required but No Subscription Check) */}
        <Route
          path="/wallet"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <WalletPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Billing Dashboard (Auth Required but No Subscription Check) */}
        <Route
          path="/billing"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <BillingDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Change Plan Page (Auth Required but No Subscription Check) */}
        <Route
          path="/subscription/change-plan"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <ChangePlanPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Create Subscription Page */}
        <Route
          path="/create-subscription"
          element={
            <ProtectedRoute
              roles={[AppRole.OrganizationOwner, AppRole.TeamMember]}
            >
              <AppLayout>
                <CreateSubscriptionPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Debug: LinkedIn Share Preview */}
        <Route
          path="/linkedin-share-preview"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LinkedInSharePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="text-center mt-20 text-text">Page Not Found</div>
          }
        />
      </Routes>
    </Router>
  )
}
